/**
 * รูปหน้าตัวละครมุมซ้ายบน
 *
 * วาดจากโมเดลชิบิตัวจริง (`createChibi`) ไม่ใช่รูปวาดแยกต่างหาก
 * เปลี่ยนสีผมหรือชุดใน `classes.json` แล้วรูปนี้เปลี่ยนตามเองทันที ไม่ต้องมาวาดใหม่
 *
 * **ใช้ Engine ของตัวเองบน canvas เล็ก ๆ** แยกจากฉากหลัก เพราะกล้องคนละตัว
 *
 * **วาดเฉพาะตอนมีอะไรเปลี่ยน ไม่ใช่ทุกเฟรม**
 * ตัวละครกระพริบตาและหันซ้ายขวาเป็นช่วงสั้น ๆ แล้วหยุดนิ่ง
 * ระหว่างที่นิ่งไม่เรนเดอร์เลยสักเฟรม เฉลี่ยแล้วจึงเหลือราวสี่ห้าเฟรมต่อวินาที
 * (ถ้าปล่อยให้เรนเดอร์ต่อเนื่อง จะเป็นการสลับ WebGL context ทุกเฟรม ซึ่งแพงบนมือถือ)
 *
 * ถ้าวันหนึ่งอยากให้ขยับเต็มที่ ควรเลิกใช้ context ที่สอง แล้วใช้กล้องตัวที่สอง
 * ในฉากเดียวกันพร้อม layer mask แทน ไม่ใช่เพิ่มความถี่ของที่นี่
 */

import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Color3, Color4, Vector3 } from "@babylonjs/core/Maths/math";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import {
  DEFAULT_GENDER,
  type CharacterLook, type ClassLook,
  type EquipLook,
  type Gender,
} from "@wickhaven/shared";
import { createChibi, setGear, type ChibiModel } from "./models.js";

/** วาดต่อเนื่องกี่วินาทีตอนเริ่ม ให้ shader โหลดทันก่อนจะเข้าโหมดประหยัด */
const WARMUP_SEC = 2;

/** หลับตาอยู่นานแค่ไหน (วินาที) */
const BLINK_SEC = 0.12;
/** เว้นช่วงระหว่างกระพริบตา [ต่ำสุด, สูงสุด] */
const BLINK_GAP: [number, number] = [2.4, 6];
/** เว้นช่วงระหว่างการหันหน้า */
const GLANCE_GAP: [number, number] = [5, 11];
/** หันไปแล้วค้างไว้นานแค่ไหนก่อนหันกลับ */
const GLANCE_HOLD = 1.1;
/** ใช้เวลาหันกี่วินาที */
const GLANCE_TURN = 0.55;
/** หันไปจากท่าปกติกี่เรเดียน */
const GLANCE_AMOUNT = 0.42;
/** ท่ายืนปกติ เอียงเล็กน้อยดูมีชีวิตกว่ายืนตรงเป๊ะ */
const BASE_TURN = 0.3;

const between = ([lo, hi]: [number, number]): number => lo + Math.random() * (hi - lo);

/** นุ่มนวลหัวท้าย ไม่กระตุกตอนเริ่มและหยุด */
const ease = (t: number): number => t * t * (3 - 2 * t);

export interface Portrait {
  /** เรียกทุกเฟรมจากลูปหลัก ข้างในตัดสินเองว่าเฟรมนี้ต้องวาดไหม */
  tick(dt: number): void;
  /** เปลี่ยนอาชีพและของที่สวมอยู่ */
  setLook(look: ClassLook, gear?: EquipLook[]): void;
  dispose(): void;
}

export function createPortrait(
  canvas: HTMLCanvasElement,
  look: ClassLook,
  gender: Gender = DEFAULT_GENDER,
  /** หน้าตาที่เลือกเอง (shared/looks.ts) */
  custom?: CharacterLook,
): Portrait {
  // ปิด antialias รูปเล็กมากจนไม่คุ้มค่าประมวลผล
  const engine = new Engine(canvas, false, { preserveDrawingBuffer: false }, false);
  const scene = new Scene(engine);
  // พื้นหลังโปร่ง ให้เห็นกรอบของแผง HUD ข้างหลัง
  scene.clearColor = new Color4(0, 0, 0, 0);

  // เล็งที่หัว (โมเดลวางเท้าไว้ที่ y = 0 หัวอยู่ราว 1.6)
  // alpha = PI/2 คือมองจากด้าน +Z ซึ่งเป็นด้านที่ตาของชิบิหันไป
  const cam = new ArcRotateCamera("pcam", Math.PI / 2, 1.3, 1.9, new Vector3(0, 1.62, 0), scene);
  cam.fov = 0.8;

  const hemi = new HemisphericLight("phemi", new Vector3(0.2, 1, -0.3), scene);
  hemi.intensity = 0.95;
  hemi.groundColor = new Color3(0.4, 0.42, 0.5);
  const key = new DirectionalLight("pkey", new Vector3(-0.4, -0.7, 1), scene);
  key.intensity = 0.7;

  let model: ChibiModel;
  /** ตาและไฮไลต์ในตา พร้อมความสูงเดิม ใช้บีบให้แบนตอนกระพริบ */
  let eyes: Array<{ mesh: Mesh; openY: number }> = [];

  let warmup = WARMUP_SEC;
  let blinkIn = between(BLINK_GAP);
  let blinkLeft = 0;
  let glanceIn = between(GLANCE_GAP);
  /** ความคืบของการหัน 0 = ยังไม่หัน, 1 = หันสุด แล้วค่อยลดกลับ */
  let glanceT = 0;
  let glanceDir = 1;
  let glancePhase: "none" | "out" | "hold" | "back" = "none";
  let holdLeft = 0;

  function build(l: ClassLook, gear: EquipLook[] = []): void {
    model = createChibi(scene, l, undefined, gender, custom);
    setGear(scene, model, gear);
    // รูปหน้าเห็นแค่ช่วงหัว อาวุธอยู่นอกเฟรมอยู่แล้ว ไม่ต้องโชว์

    model.root.rotation.y = BASE_TURN;
    eyes = model.root
      .getChildMeshes()
      .filter((m): m is Mesh => m.name === "eye" || m.name === "hl")
      .map((mesh) => ({ mesh, openY: mesh.scaling.y }));
  }
  build(look);

  /** ตั้งความเปิดของตา 1 = ลืมเต็มที่ 0 = หลับ */
  function setEyes(open: number): void {
    for (const e of eyes) e.mesh.scaling.y = e.openY * Math.max(0.06, open);
  }

  return {
    tick(dt) {
      // จำกัดไม่ให้ก้าวใหญ่เกิน เผื่อเพิ่งสลับแท็บกลับมาแล้ว dt พุ่ง
      const step = Math.min(dt, 0.1);
      let dirty = false;

      if (warmup > 0) {
        warmup -= step;
        dirty = true;
      }

      // ---- กระพริบตา ----
      if (blinkLeft > 0) {
        blinkLeft -= step;
        if (blinkLeft <= 0) {
          setEyes(1);
          blinkIn = between(BLINK_GAP);
        }
        dirty = true;
      } else {
        blinkIn -= step;
        if (blinkIn <= 0) {
          blinkLeft = BLINK_SEC;
          setEyes(0);
          dirty = true;
        }
      }

      // ---- หันซ้ายขวา ----
      if (glancePhase === "none") {
        glanceIn -= step;
        if (glanceIn <= 0) {
          glancePhase = "out";
          glanceDir = Math.random() < 0.5 ? -1 : 1;
        }
      } else if (glancePhase === "out") {
        glanceT = Math.min(1, glanceT + step / GLANCE_TURN);
        if (glanceT >= 1) {
          glancePhase = "hold";
          holdLeft = GLANCE_HOLD;
        }
        dirty = true;
      } else if (glancePhase === "hold") {
        holdLeft -= step;
        if (holdLeft <= 0) glancePhase = "back";
      } else {
        glanceT = Math.max(0, glanceT - step / GLANCE_TURN);
        if (glanceT <= 0) {
          glancePhase = "none";
          glanceIn = between(GLANCE_GAP);
        }
        dirty = true;
      }

      if (dirty) {
        model.root.rotation.y = BASE_TURN + ease(glanceT) * GLANCE_AMOUNT * glanceDir;
        scene.render();
      }
    },

    setLook(next, gear = []) {
      // ไม่ลบวัสดุ มันถูกแคชใช้ร่วมกันทั้งฉาก (ดู `mat` ใน models.ts)
      model.root.dispose(false, false);
      build(next, gear);
      warmup = WARMUP_SEC;
    },

    dispose() {
      scene.dispose();
      engine.dispose();
    },
  };
}
